import { useEffect, useRef } from 'react';
import { useGoogleMap } from '@react-google-maps/api';

// Polygon drawing for the Geo Boundary map.
//
// Google removed the Maps JavaScript API DrawingManager in v3.65
// (https://developers.google.com/maps/deprecations), which crashed the page when the old
// @react-google-maps/api <DrawingManager> mounted. This is a drop-in manual replacement built on
// the still-supported core API: while in drawing mode the user clicks to add polygon vertices
// (shown live as a polyline) and double-clicks to finish. On finish we build a real
// google.maps.Polygon and hand it to `drawingCompleted` — the same object the previous
// onPolygonComplete callback received (the consumer reads `.getPath()` then drops it with
// `.setMap(null)`), so nothing downstream changes.
const DrawingGeoBoundary = ({ isDrawingMode = false, drawingCompleted }) => {
  const map = useGoogleMap();
  const pathRef = useRef([]); // accumulated vertices (LatLng) for the polygon in progress
  const polylineRef = useRef(null); // live preview of the path
  const listenersRef = useRef([]);

  useEffect(() => {
    const g = window.google && window.google.maps;
    if (!map || !g) return undefined;

    const cleanup = () => {
      listenersRef.current.forEach((l) => l.remove());
      listenersRef.current = [];
      if (polylineRef.current) {
        polylineRef.current.setMap(null);
        polylineRef.current = null;
      }
      pathRef.current = [];
    };

    if (!isDrawingMode) {
      cleanup();
      return undefined;
    }

    // Live preview of the boundary being drawn.
    polylineRef.current = new g.Polyline({
      map,
      path: [],
      strokeColor: '#a576fb',
      strokeWeight: 3,
      clickable: false,
      zIndex: 1,
    });

    // Two clicked points are "the same" when they map to the same spot (a double-click adds a
    // trailing click at the finish location); collapse those so the final vertex isn't duplicated.
    const approxEqual = (a, b) =>
      a && b && Math.abs(a.lat() - b.lat()) < 1e-6 && Math.abs(a.lng() - b.lng()) < 1e-6;

    const finish = () => {
      const path = pathRef.current;
      if (path.length < 3) return; // need at least a triangle; otherwise keep drawing

      const polygon = new g.Polygon({
        paths: path,
        fillColor: '#d7a6fd',
        strokeColor: '#a576fb',
        strokeWeight: 3,
        clickable: false,
        editable: false,
        zIndex: 1,
      });

      // Hand the completed polygon to the consumer (which reads getPath() / computes the centre),
      // then remove it — the consumer re-renders the saved shape itself.
      drawingCompleted(polygon);
      polygon.setMap(null);
      cleanup();
    };

    const onClick = (e) => {
      pathRef.current.push(e.latLng);
      polylineRef.current.setPath(pathRef.current);
    };

    const onDblClick = (e) => {
      const path = pathRef.current;
      // Drop the trailing click(s) the double-click produced at this spot, keep a single vertex.
      while (path.length && approxEqual(path[path.length - 1], e.latLng)) path.pop();
      path.push(e.latLng);
      finish();
    };

    listenersRef.current = [
      map.addListener('click', onClick),
      map.addListener('dblclick', onDblClick),
    ];
    // Stop the default double-click-to-zoom so finishing the polygon doesn't also zoom the map.
    map.setOptions({ disableDoubleClickZoom: true });

    return () => {
      map.setOptions({ disableDoubleClickZoom: false });
      cleanup();
    };
    // drawingCompleted is a stable callback from the parent; re-running on it would tear down an
    // in-progress drawing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, isDrawingMode]);

  return null;
};

export default DrawingGeoBoundary;
